import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const scrollPositions = new Map();

const useScrollToTop = () => {
    const location = useLocation();
    const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
    const prevPathnameRef = useRef(location.pathname);
    const scrollTimerRef = useRef(null);

    // Save scroll continuously during scrolling
    useEffect(() => {
        const handleScroll = () => {
            const y = window.pageYOffset || window.scrollY || document.documentElement.scrollTop || 0;
            if (y > 0) {
                scrollPositions.set(location.pathname, y);
                try {
                    sessionStorage.setItem(`ukiyo_scroll_${location.pathname}`, y.toString());
                } catch (e) {}
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [location.pathname]);

    useLayoutEffect(() => {
        if (scrollTimerRef.current) {
            clearInterval(scrollTimerRef.current);
            scrollTimerRef.current = null;
        }

        const isPop = navType === 'POP';
        const isReplace = navType === 'REPLACE';
        const isSamePath = prevPathnameRef.current === location.pathname;
        const previousPath = prevPathnameRef.current;
        prevPathnameRef.current = location.pathname;

        // If replacing query params on the same page, keep scroll
        if (isReplace && isSamePath) {
            return;
        }

        // Dedicated product page always opens at top
        if (location.pathname.startsWith('/product/')) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            return;
        }

        // On catalog pages (/products, /products/category/*), let ShopPage handle its own restoration
        if (location.pathname === '/products' || location.pathname.startsWith('/products/category/')) {
            // If user came from a product page, skip forced scroll-to-top
            if (previousPath && previousPath.startsWith('/product/')) {
                return;
            }
            if (isPop) {
                return;
            }
        }

        if (isPop) {
            let targetY = scrollPositions.get(location.pathname);
            if (targetY === undefined) {
                try {
                    const stored = sessionStorage.getItem(`ukiyo_scroll_${location.pathname}`);
                    if (stored !== null) targetY = parseFloat(stored);
                } catch (e) {}
            }

            if (targetY !== undefined && targetY > 0) {
                window.scrollTo({ top: targetY, left: 0, behavior: 'instant' });

                let attempts = 0;
                scrollTimerRef.current = setInterval(() => {
                    attempts++;
                    const maxScroll = Math.max(
                        document.documentElement.scrollHeight - window.innerHeight,
                        0
                    );
                    const currentY = window.pageYOffset || window.scrollY || 0;

                    if (Math.abs(currentY - targetY) < 15 || attempts > 25) {
                        clearInterval(scrollTimerRef.current);
                        scrollTimerRef.current = null;
                    } else {
                        window.scrollTo({
                            top: Math.min(targetY, maxScroll),
                            left: 0,
                            behavior: 'instant'
                        });
                    }
                }, 35);
                return;
            }
        }

        // Fresh top-level navigation to other pages
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [location.pathname, navType]);

    return null;
};

export default useScrollToTop;
